export interface ViewportRulerRendererContext {
  topCanvas: HTMLCanvasElement;
  leftCanvas: HTMLCanvasElement;
  scale: number;
  scrollLeft: number;
  scrollTop: number;
  logicalWidth: number;
  logicalHeight: number;
}

type AxisOrientation = 'horizontal' | 'vertical';

const MINOR_TICK_STEP = 10;
const MEDIUM_TICK_STEP = 50;
const MAJOR_TICK_STEP = 100;
const LABEL_STEPS = [100, 200, 400, 800, 1600, 3200, 6400, 12800];
const IDEAL_LABEL_SPACING_PX = 96;
const MIN_MINOR_VISIBLE_SPACING_PX = 6;
const RULER_BACKGROUND_FILL = 'rgba(43, 55, 74, 0.985)';
const RULER_HIGHLIGHT = 'rgba(255, 255, 255, 0.03)';
const RULER_EDGE = 'rgba(148, 163, 184, 0.46)';
const RULER_INNER_EDGE = 'rgba(15, 23, 42, 0.16)';
const MINOR_TICK_COLOR = 'rgba(248, 250, 252, 0.18)';
const MEDIUM_TICK_COLOR = 'rgba(248, 250, 252, 0.32)';
const MAJOR_TICK_COLOR = 'rgba(248, 250, 252, 0.58)';
const LABEL_COLOR = 'rgba(248, 250, 252, 0.96)';
const LABEL_FONT = '700 8px "Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif';

export class ViewportRulerRenderer {
  private animationFrameId: number | null = null;

  constructor(private readonly resolveContext: () => ViewportRulerRendererContext | null) {}

  scheduleRender(): void {
    if (typeof window === 'undefined' || typeof requestAnimationFrame === 'undefined') {
      this.renderNow();
      return;
    }

    if (this.animationFrameId !== null) {
      return;
    }

    this.animationFrameId = requestAnimationFrame(() => {
      this.animationFrameId = null;
      this.renderNow();
    });
  }

  disconnect(): void {
    if (this.animationFrameId !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private renderNow(): void {
    const context = this.resolveContext();
    if (!context) {
      return;
    }

    this.renderAxis({
      canvas: context.topCanvas,
      orientation: 'horizontal',
      scrollOffset: context.scrollLeft,
      logicalLength: context.logicalWidth,
      scale: context.scale
    });

    this.renderAxis({
      canvas: context.leftCanvas,
      orientation: 'vertical',
      scrollOffset: context.scrollTop,
      logicalLength: context.logicalHeight,
      scale: context.scale
    });
  }

  private renderAxis(options: {
    canvas: HTMLCanvasElement;
    orientation: AxisOrientation;
    scrollOffset: number;
    logicalLength: number;
    scale: number;
  }): void {
    const { canvas, orientation, scrollOffset, logicalLength } = options;
    const scale = Math.max(options.scale || 1, 0.05);
    const configured = this.configureCanvas(canvas);
    if (!configured) {
      return;
    }

    const { drawingContext, cssWidth, cssHeight } = configured;
    const axisLength = orientation === 'horizontal' ? cssWidth : cssHeight;

    this.drawBackground(drawingContext, cssWidth, cssHeight, orientation);

    const labelStep = resolveLabelStep(scale);
    const shouldDrawMinorTicks = MINOR_TICK_STEP * scale >= MIN_MINOR_VISIBLE_SPACING_PX;
    const tickStep = shouldDrawMinorTicks ? MINOR_TICK_STEP : MEDIUM_TICK_STEP;
    const visibleLogicalRange = axisLength / scale;
    const startLogical = Math.max(0, Math.floor(scrollOffset / tickStep) * tickStep);
    const endLogical = Math.min(
      logicalLength,
      Math.ceil((scrollOffset + visibleLogicalRange) / tickStep) * tickStep
    );

    drawingContext.font = LABEL_FONT;
    drawingContext.fillStyle = LABEL_COLOR;
    drawingContext.textBaseline = 'top';
    drawingContext.lineWidth = 1;

    for (let logicalValue = startLogical; logicalValue <= endLogical; logicalValue += tickStep) {
      const visiblePosition = (logicalValue - scrollOffset) * scale;
      if (visiblePosition < -tickStep || visiblePosition > axisLength + tickStep) {
        continue;
      }

      const isMajorTick = logicalValue % MAJOR_TICK_STEP === 0;
      const isMediumTick = !isMajorTick && logicalValue % MEDIUM_TICK_STEP === 0;

      if (!shouldDrawMinorTicks && !isMediumTick && !isMajorTick) {
        continue;
      }

      this.drawTick(drawingContext, {
        orientation,
        position: visiblePosition,
        cssWidth,
        cssHeight,
        emphasis: isMajorTick ? 'major' : isMediumTick ? 'medium' : 'minor'
      });

      if (logicalValue % labelStep === 0) {
        this.drawLabel(drawingContext, {
          orientation,
          logicalValue,
          position: visiblePosition,
          cssWidth,
          cssHeight
        });
      }
    }
  }

  private configureCanvas(canvas: HTMLCanvasElement): {
    drawingContext: CanvasRenderingContext2D;
    cssWidth: number;
    cssHeight: number;
  } | null {
    const cssWidth = Math.max(1, Math.round(canvas.clientWidth));
    const cssHeight = Math.max(1, Math.round(canvas.clientHeight));
    const pixelRatio = typeof window === 'undefined' ? 1 : Math.max(1, window.devicePixelRatio || 1);
    const targetWidth = Math.max(1, Math.round(cssWidth * pixelRatio));
    const targetHeight = Math.max(1, Math.round(cssHeight * pixelRatio));

    if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
      canvas.width = targetWidth;
      canvas.height = targetHeight;
    }

    const drawingContext = canvas.getContext('2d');
    if (!drawingContext) {
      return null;
    }

    drawingContext.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    drawingContext.clearRect(0, 0, cssWidth, cssHeight);

    return {
      drawingContext,
      cssWidth,
      cssHeight
    };
  }

  private drawBackground(
    drawingContext: CanvasRenderingContext2D,
    cssWidth: number,
    cssHeight: number,
    orientation: AxisOrientation
  ): void {
    drawingContext.fillStyle = RULER_BACKGROUND_FILL;
    drawingContext.fillRect(0, 0, cssWidth, cssHeight);

    drawingContext.strokeStyle = RULER_HIGHLIGHT;
    drawingContext.beginPath();
    if (orientation === 'horizontal') {
      drawingContext.moveTo(0, 0.5);
      drawingContext.lineTo(cssWidth, 0.5);
    } else {
      drawingContext.moveTo(0.5, 0);
      drawingContext.lineTo(0.5, cssHeight);
    }
    drawingContext.stroke();

    drawingContext.strokeStyle = RULER_INNER_EDGE;
    drawingContext.beginPath();
    if (orientation === 'horizontal') {
      drawingContext.moveTo(0, cssHeight - 1.5);
      drawingContext.lineTo(cssWidth, cssHeight - 1.5);
    } else {
      drawingContext.moveTo(cssWidth - 1.5, 0);
      drawingContext.lineTo(cssWidth - 1.5, cssHeight);
    }
    drawingContext.stroke();

    drawingContext.strokeStyle = RULER_EDGE;
    drawingContext.beginPath();
    if (orientation === 'horizontal') {
      drawingContext.moveTo(0, cssHeight - 0.5);
      drawingContext.lineTo(cssWidth, cssHeight - 0.5);
    } else {
      drawingContext.moveTo(cssWidth - 0.5, 0);
      drawingContext.lineTo(cssWidth - 0.5, cssHeight);
    }
    drawingContext.stroke();
  }

  private drawTick(
    drawingContext: CanvasRenderingContext2D,
    options: {
      orientation: AxisOrientation;
      position: number;
      cssWidth: number;
      cssHeight: number;
      emphasis: 'minor' | 'medium' | 'major';
    }
  ): void {
    const { orientation, position, cssWidth, cssHeight, emphasis } = options;
    const crispPosition = Math.round(position) + 0.5;
    const tickLength = emphasis === 'major' ? 10 : emphasis === 'medium' ? 7 : 4;

    drawingContext.strokeStyle =
      emphasis === 'major' ? MAJOR_TICK_COLOR : emphasis === 'medium' ? MEDIUM_TICK_COLOR : MINOR_TICK_COLOR;
    drawingContext.beginPath();

    if (orientation === 'horizontal') {
      drawingContext.moveTo(crispPosition, cssHeight);
      drawingContext.lineTo(crispPosition, cssHeight - tickLength);
    } else {
      drawingContext.moveTo(cssWidth, crispPosition);
      drawingContext.lineTo(cssWidth - tickLength, crispPosition);
    }

    drawingContext.stroke();
  }

  private drawLabel(
    drawingContext: CanvasRenderingContext2D,
    options: {
      orientation: AxisOrientation;
      logicalValue: number;
      position: number;
      cssWidth: number;
      cssHeight: number;
    }
  ): void {
    const { orientation, logicalValue, position, cssWidth, cssHeight } = options;
    const label = String(logicalValue);

    if (orientation === 'horizontal') {
      drawingContext.textAlign = logicalValue === 0 ? 'left' : 'center';
      const x = logicalValue === 0 ? 3 : position;
      drawingContext.fillText(label, x, 1);
      return;
    }

    drawingContext.save();
    drawingContext.translate(3, Math.min(Math.max(8, position), cssHeight - 2));
    drawingContext.rotate(-Math.PI / 2);
    drawingContext.textAlign = 'left';
    drawingContext.fillText(label, 0, 0);
    drawingContext.restore();
  }
}

function resolveLabelStep(scale: number): number {
  const desiredLogicalSpacing = IDEAL_LABEL_SPACING_PX / Math.max(scale, 0.05);
  for (const candidate of LABEL_STEPS) {
    if (candidate >= desiredLogicalSpacing) {
      return candidate;
    }
  }

  return LABEL_STEPS[LABEL_STEPS.length - 1];
}
